import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartCrack, Sparkles, Crown, ShoppingCart, Check, Gift, ArrowDown, Star, X } from 'lucide-react';
// Purchase notification names and locations for social proof
const purchaseNotifications = [{
  name: 'María G.',
  location: 'Miami, FL'
}, {
  name: 'Carmen R.',
  location: 'Los Angeles, CA'
}, {
  name: 'Isabella M.',
  location: 'Houston, TX'
}, {
  name: 'Sofía L.',
  location: 'New York, NY'
}, {
  name: 'Valentina P.',
  location: 'Chicago, IL'
}, {
  name: 'Camila S.',
  location: 'Phoenix, AZ'
}, {
  name: 'Lucía F.',
  location: 'San Antonio, TX'
}, {
  name: 'Daniela V.',
  location: 'San Diego, CA'
}];
export function ToxicDetoxLanding() {
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(0);
  const purchaseSectionRef = useRef<HTMLElement>(null);
  const scrollToPurchase = () => {
    purchaseSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // Purchase notifications
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentNotification(prev => (prev + 1) % purchaseNotifications.length);
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 4000);
    }, 7000);
    return () => clearInterval(interval);
  }, []);
  return <div className="min-h-screen w-full bg-gradient-to-b from-rose-50 via-purple-50 to-amber-50" data-id="element-4">
      {/* Sticky Header Button */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-rose-600 to-purple-600 shadow-lg" data-id="element-5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-center" data-id="element-6">
          <motion.button onClick={scrollToPurchase} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="bg-white text-purple-900 px-8 py-3 rounded-full text-[clamp(1rem,2.5vw,1.25rem)] font-bold shadow-xl hover:shadow-2xl transition-all duration-300" data-id="element-7">
            QUIERO EMPEZAR AHORA
          </motion.button>
        </div>
      </div>

      {/* Purchase Notification Popup */}
      <AnimatePresence data-id="element-8">
        {showNotification && <motion.div initial={{
        x: -400,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: -400,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 100,
        damping: 20
      }} className="fixed bottom-6 left-6 z-50 bg-white rounded-lg shadow-2xl p-4 max-w-sm border-l-4 border-green-500" data-id="element-9">
            <div className="flex items-start gap-3" data-id="element-10">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center" data-id="element-11">
                <Check className="w-6 h-6 text-green-600" data-id="element-12" />
              </div>
              <div className="flex-1" data-id="element-13">
                <p className="font-semibold text-gray-900 text-sm" data-id="element-14">
                  {purchaseNotifications[currentNotification].name}
                </p>
                <p className="text-xs text-gray-600" data-id="element-15">
                  {purchaseNotifications[currentNotification].location}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-16">
                  Acaba de comprar Toxic Relationship Detox™
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* HERO: No llegaste aquí por casualidad */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16 mt-20" data-id="element-17">
        <div className="max-w-5xl mx-auto" data-id="element-18">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-19">
            {/* Title in Styled Box */}
            <div className="bg-purple-900 rounded-3xl px-8 py-6 mb-8 shadow-2xl" data-id="element-20">
              <h2 className="text-[clamp(1.75rem,5vw,3.5rem)] font-bold text-rose-50 text-center leading-tight" data-id="element-21">
                NO LLEGASTE AQUÍ POR CASUALIDAD
              </h2>
            </div>

            {/* New Broken Heart Image */}
            <div className="mb-8" data-id="element-22">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4mc6dzRczhK4WqenVGELuC/ChatGPT_Image_27_nov_2025%2C_12_01_49_p.m..png" alt="Sanando un corazón roto" className="w-full h-auto object-contain rounded-2xl shadow-2xl" data-id="element-23" />
            </div>

            {/* Text Content */}
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800 mb-12" data-id="element-24">
              <p data-id="element-25">
                Llegaste aquí porque tu corazón está cansado. Porque tu cuerpo
                vive en alerta. Y porque tu alma lleva tiempo susurrándote: "Ya
                basta."
              </p>
              <p data-id="element-26">
                Tal vez ya intentaste irte. Tal vez te fuiste y regresaste. Tal
                vez sigues ahí, justificando lo que duele.
              </p>
              <p data-id="element-27">Pero en el fondo sabes la verdad:</p>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-rose-700 text-center my-8" data-id="element-28">
                Esto no es amor. Es adicción emocional envuelta en culpa.
              </p>
              <p data-id="element-29">Y ya no quieres seguir sobreviviendo en nombre del "amor".</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Title + Book Cover */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-30">
        <div className="max-w-6xl mx-auto" data-id="element-31">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-8" data-id="element-32">
            <h1 className="font-serif text-[clamp(2rem,6vw,4rem)] leading-tight mb-4 text-purple-900" data-id="element-33">
              TOXIC RELATIONSHIP DETOX™ — LATINA EDITION
            </h1>
            <div className="text-[clamp(1.25rem,3vw,2rem)] text-rose-600 font-light mb-8" data-id="element-34">
              Un proceso de sanación creado para mujeres latinas
            </div>
            <p className="text-[clamp(1.125rem,2.5vw,1.5rem)] text-gray-700 mb-6" data-id="element-35">
              Este no es un libro genérico ni frases motivacionales.
            </p>
            <p className="text-[clamp(1.125rem,2.5vw,1.5rem)] text-gray-700 mb-6" data-id="element-36">
              Es un detox emocional guiado, creado para mujeres latinas que
              desean:
            </p>
            <ul className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-700 space-y-2 mb-8 text-left max-w-2xl mx-auto" data-id="element-37">
              <li className="flex items-start" data-id="element-38">
                <span className="text-rose-600 mr-3" data-id="element-39">✔</span>
                <span data-id="element-40">Romper vínculos tóxicos</span>
              </li>
              <li className="flex items-start" data-id="element-41">
                <span className="text-rose-600 mr-3" data-id="element-42">✔</span>
                <span data-id="element-43">Sanar heridas profundas</span>
              </li>
              <li className="flex items-start" data-id="element-44">
                <span className="text-rose-600 mr-3" data-id="element-45">✔</span>
                <span data-id="element-46">Salir del ciclo de ida y vuelta</span>
              </li>
              <li className="flex items-start" data-id="element-47">
                <span className="text-rose-600 mr-3" data-id="element-48">✔</span>
                <span data-id="element-49">Recuperar su identidad</span>
              </li>
              <li className="flex items-start" data-id="element-50">
                <span className="text-rose-600 mr-3" data-id="element-51">✔</span>
                <span data-id="element-52">Volver a sentir paz</span>
              </li>
              <li className="flex items-start" data-id="element-53">
                <span className="text-rose-600 mr-3" data-id="element-54">✔</span>
                <span data-id="element-55">Renacer como Reina Reborn™</span>
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex justify-center" data-id="element-56">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3x5bBtCaSuPuiPb8bCmZhS/Generated_Image_November_17%2C_2025_-_1_26PM.png" alt="Toxic Relationship Detox Book Cover" className="w-full max-w-md h-auto object-contain shadow-2xl rounded-lg" data-id="element-57" />
          </motion.div>
        </div>
      </section>

      {/* Si esto te pasa */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-58">
        <div className="max-w-4xl mx-auto" data-id="element-59">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-60">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-serif text-purple-900 mb-8 text-center" data-id="element-61">
              Si esto te pasa, no estás sola
            </h2>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-62">
              <p data-id="element-63">Revisas el teléfono antes de respirar.</p>
              <p data-id="element-64">
                "¿Me escribió?" "¿Por qué estuvo en línea y no contestó?" "¿Por
                qué siempre termino dudando de mí?"
              </p>
              <p data-id="element-65">Vives pendiente de qué versión de él aparecerá hoy:</p>
              <p data-id="element-66">Y al final siempre el siempre te dice lo mismo:</p>
              <p className="italic" data-id="element-67">
                "Estás exagerando." "Estás loca." "Eso nunca pasó."
              </p>
              <p className="font-semibold text-rose-700" data-id="element-68">
                Hasta que empiezas a dudar de ti.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Couple Arguing Image */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-69">
        <div className="max-w-5xl mx-auto" data-id="element-70">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-71">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4nT8V7HT8zeXrPtPBiV8HE/ChatGPT_Image_24_nov_2025%2C_10_41_31_p.m..png" alt="La realidad de una relación tóxica" className="w-full h-auto object-contain rounded-2xl shadow-2xl" data-id="element-72" />
          </motion.div>
        </div>
      </section>

      {/* Esto no es solo una relación */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-73">
        <div className="max-w-4xl mx-auto" data-id="element-74">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-75">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-serif text-purple-900 mb-8 text-center" data-id="element-76">
              Esto no es solo una relación
            </h2>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-77">
              <p data-id="element-78">Si fuera solo él, ya te hubieras ido.</p>
              <p data-id="element-79">Pero estás luchando contra:</p>
              <ul className="list-none space-y-3 my-8 pl-6" data-id="element-80">
                <li className="text-purple-700" data-id="element-81">culpa familiar</li>
                <li className="text-purple-700" data-id="element-82">miedo a estar sola</li>
                <li className="text-purple-700" data-id="element-83">presión cultural</li>
                <li className="text-purple-700" data-id="element-84">
                  espiritualidad mal entendida
                </li>
                <li className="text-purple-700" data-id="element-85">
                  un sistema nervioso acostumbrado al caos
                </li>
              </ul>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 text-center my-8" data-id="element-86">
                No eres débil. Nadie te enseñó cómo salir de esto de forma
                segura.
              </p>
              <p className="text-center text-[clamp(1.5rem,3.5vw,2rem)] text-rose-600 font-semibold" data-id="element-87">
                Hasta hoy.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 7 Steps */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-88">
        <div className="max-w-5xl mx-auto" data-id="element-89">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-90">
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-serif text-purple-900 mb-6 text-center" data-id="element-91">
              El Sistema Detox en 7 Pasos
            </h2>
            <p className="text-[clamp(1.125rem,2.5vw,1.5rem)] text-gray-700 mb-12 text-center" data-id="element-92">
              Este proceso te acompaña paso a paso, sin obligarte a ser fuerte.
            </p>
            <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 mb-8 text-center" data-id="element-93">
              Tu mapa de sanación:
            </p>
            <div className="space-y-8" data-id="element-94">
              {[{
              number: '1',
              title: 'Reset de No Contacto',
              description: 'Sostén límites sin culpa ni ansiedad.'
            }, {
              number: '2',
              title: 'Depuración Digital',
              description: 'Elimina disparadores emocionales.'
            }, {
              number: '3',
              title: 'Liberación Emocional',
              description: 'Ejercicios guiados para soltar rabia, tristeza y vergüenza.'
            }, {
              number: '4',
              title: 'Ritual de Cierre Energético',
              description: 'Cierra el ciclo desde dentro.'
            }, {
              number: '5',
              title: 'Reprogramación del Subconsciente',
              description: 'Rompe creencias que te mantienen atrapada.'
            }, {
              number: '6',
              title: 'Sanación del Trauma en el Cuerpo',
              description: 'Calma ansiedad, hipervigilancia y tensión.'
            }, {
              number: '7',
              title: 'Renacimiento de Autoestima',
              description: 'Recupera tu identidad y reabre tu corazón desde la seguridad.'
            }].map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-gradient-to-r from-rose-50 to-purple-50 rounded-xl p-6 sm:p-8 shadow-lg border-l-4 border-rose-400" data-id="element-95">
                  <div className="flex items-start gap-4" data-id="element-96">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-rose-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl sm:text-2xl" data-id="element-97">
                      {step.number}
                    </div>
                    <div className="flex-1" data-id="element-98">
                      <h3 className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 mb-2" data-id="element-99">
                        {step.title}
                      </h3>
                      <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-700 leading-relaxed" data-id="element-100">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-101">
        <div className="max-w-4xl mx-auto" data-id="element-102">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-103">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-serif text-purple-900 mb-8 text-center" data-id="element-104">
              ¿Qué puedes empezar a sentir en semanas?
            </h2>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-105">
              <ul className="list-none space-y-4 my-8 pl-6" data-id="element-106">
                <li className="flex items-start" data-id="element-107">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-108">→</span>
                  <span data-id="element-109">Pensamientos más claros</span>
                </li>
                <li className="flex items-start" data-id="element-110">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-111">→</span>
                  <span data-id="element-112">Menos necesidad de revisar el teléfono</span>
                </li>
                <li className="flex items-start" data-id="element-113">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-114">→</span>
                  <span data-id="element-115">Más calma en el cuerpo</span>
                </li>
                <li className="flex items-start" data-id="element-116">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-117">→</span>
                  <span data-id="element-118">Límites más firmes sin tanta culpa</span>
                </li>
                <li className="flex items-start" data-id="element-119">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-120">→</span>
                  <span data-id="element-121">Decisiones más conscientes</span>
                </li>
                <li className="flex items-start" data-id="element-122">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-123">→</span>
                  <span data-id="element-124">Más respeto propio</span>
                </li>
                <li className="flex items-start" data-id="element-125">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-126">→</span>
                  <span data-id="element-127">Menos idealización</span>
                </li>
                <li className="flex items-start" data-id="element-128">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-129">→</span>
                  <span data-id="element-130">Más conexión contigo</span>
                </li>
              </ul>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 text-center my-8" data-id="element-131">
                No porque seas "fuerte". Sino porque por fin tienes un camino
                que sostiene tu proceso.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-132">
        <div className="max-w-6xl mx-auto" data-id="element-133">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-134">
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-serif text-purple-900 mb-12 text-center" data-id="element-135">
              Lo que dicen mujeres que ya hicieron el Detox
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-136">
              {[{
              name: 'Andrea, 34',
              text: 'Regresé a él más de ocho veces. Este detox me dio claridad y estructura. Por primera vez sentí paz y no regresé más.'
            }, {
              name: 'Valeria, 41',
              text: 'Vivía en alerta todo el tiempo. La parte del trauma corporal me cambió. Duermo mejor y volví a sentirme yo.'
            }, {
              name: 'Sofía, 28',
              text: 'Pensé que yo era la tóxica. Entendí mi patrón y dejé de idealizar. Un día desperté y ya no sentí la necesidad de buscarlo.'
            }].map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.2
            }} className="bg-white rounded-xl p-6 sm:p-8 shadow-xl border-t-4 border-rose-400" data-id="element-137">
                  <p className="text-[clamp(1rem,2.5vw,1.125rem)] text-gray-700 leading-relaxed mb-6 italic" data-id="element-138">
                    "{testimonial.text}"
                  </p>
                  <p className="text-[clamp(1rem,2.5vw,1.125rem)] font-semibold text-purple-900" data-id="element-139">
                    {testimonial.name}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Purchase Section */}
      <section ref={purchaseSectionRef} className="w-full px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-purple-900 via-rose-900 to-purple-900" data-id="element-140">
        <div className="max-w-6xl mx-auto" data-id="element-141">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-142">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0" data-id="element-143">
              {/* Left: Product Image */}
              <div className="bg-gradient-to-br from-rose-50 to-purple-50 p-8 sm:p-12 flex items-center justify-center" data-id="element-144">
                <motion.img initial={{
                scale: 0.9,
                opacity: 0
              }} whileInView={{
                scale: 1,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.2
              }} src="https://uploadthingy.s3.us-west-1.amazonaws.com/dkuGt92J5PUPup1iCyStpP/Image-FX-2025-11-24.jpg" alt="Toxic Relationship Detox - Digital Product" className="w-full h-auto object-contain max-w-lg" data-id="element-145" />
              </div>

              {/* Right: Purchase Details */}
              <div className="p-8 sm:p-12 flex flex-col justify-center" data-id="element-146">
                <div className="mb-8" data-id="element-147">
                  <h3 className="text-[clamp(1.75rem,4vw,2.5rem)] font-serif text-purple-900 mb-6 text-center" data-id="element-148">
                    ¿Este programa es para ti?
                  </h3>
                </div>

                {/* Is This For You Section */}
                <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-2xl p-6 mb-8 border-2 border-purple-300" data-id="element-149">
                  <ul className="space-y-2 mb-6" data-id="element-150">
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-151">
                      <span className="text-green-600 mr-2" data-id="element-152">✅</span>
                      <span data-id="element-153">Si te cuesta soltar aunque sabes que te duele</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-154">
                      <span className="text-green-600 mr-2" data-id="element-155">✅</span>
                      <span data-id="element-156">Si estás emocionalmente cansada</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-157">
                      <span className="text-green-600 mr-2" data-id="element-158">✅</span>
                      <span data-id="element-159">Si quieres paz más que intensidad</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-160">
                      <span className="text-green-600 mr-2" data-id="element-161">✅</span>
                      <span data-id="element-162">Si estás lista para mirarte con honestidad</span>
                    </li>
                  </ul>

                  <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-bold text-rose-700 mb-3" data-id="element-163">
                    ❌ No es para ti si:
                  </h5>
                  <ul className="space-y-2" data-id="element-164">
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-165">
                      <span className="text-rose-600 mr-2" data-id="element-166">•</span>
                      <span data-id="element-167">quieres cambiarlo a él</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-168">
                      <span className="text-rose-600 mr-2" data-id="element-169">•</span>
                      <span data-id="element-170">buscas una solución mágica</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-171">
                      <span className="text-rose-600 mr-2" data-id="element-172">•</span>
                      <span data-id="element-173">
                        no estás lista para responsabilizarte de tu sanación
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Pricing */}
                <div className="mb-8 text-center" data-id="element-174">
                  <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-bold text-purple-900 mb-4" data-id="element-175">
                    Inversión consciente
                  </p>
                  <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-600 mb-2" data-id="element-176">
                    Valor real:
                  </p>
                  <p className="text-[clamp(2rem,5vw,3rem)] font-bold text-gray-400 line-through mb-2" data-id="element-177">
                    $47
                  </p>
                  <p className="text-[clamp(1.25rem,3vw,1.75rem)] text-rose-600 font-bold mb-2" data-id="element-178">
                    HOY:
                  </p>
                  <div className="flex items-baseline justify-center mb-2" data-id="element-179">
                    <span className="text-[clamp(3rem,8vw,5rem)] font-bold text-purple-900" data-id="element-180">
                      $9.97
                    </span>
                  </div>
                  <p className="text-[clamp(0.875rem,2vw,1rem)] text-gray-600" data-id="element-181">
                    Pago único • Acceso inmediato
                  </p>
                </div>

                {/* Bonuses Section */}
                <div className="bg-gradient-to-r from-amber-50 to-rose-50 rounded-2xl p-6 mb-8 border-2 border-amber-300" data-id="element-182">
                  <h4 className="text-[clamp(1.25rem,3vw,1.5rem)] font-bold text-amber-700 mb-4 text-center" data-id="element-183">
                    🎁 Bonos incluidos hoy
                  </h4>

                  {/* Bonus 1 */}
                  <div className="mb-4" data-id="element-184">
                    <div className="flex items-center justify-center mb-3" data-id="element-185">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sq9uL7Ee1UGsQGj5XM5H85/ChatGPT_Image_26_nov_2025%2C_11_21_46_p.m..png" alt="Diario Emocional 30 Días" className="w-32 h-auto object-contain rounded-lg shadow-lg" data-id="element-186" />
                    </div>
                    <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-semibold text-purple-900 mb-2 text-center" data-id="element-187">
                      Diario Emocional 30 Días
                    </h5>
                  </div>

                  {/* Bonus 2 */}
                  <div className="pt-4 border-t border-amber-200" data-id="element-188">
                    <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-semibold text-purple-900 mb-2 text-center" data-id="element-189">
                      Meditación Guiada de Cierre + Afirmaciones
                    </h5>
                  </div>
                </div>

                {/* Guarantee */}
                <div className="bg-green-50 rounded-xl p-6 mb-8 border-2 border-green-200" data-id="element-190">
                  <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-bold text-green-800 mb-3 text-center" data-id="element-191">
                    🔐 Garantía de Tranquilidad
                  </h5>
                  <p className="text-[clamp(0.875rem,2vw,1rem)] text-gray-700 text-center leading-relaxed" data-id="element-192">
                    Prueba el programa. Si sientes que no es para ti, escríbenos
                    dentro de los primeros 7 días y te devolvemos tu dinero.
                  </p>
                  <p className="text-[clamp(0.875rem,2vw,1rem)] text-gray-600 text-center mt-2 italic" data-id="element-193">
                    Sin preguntas. Sin culpa. Sin explicaciones.
                  </p>
                </div>

                <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-700 text-center mb-6 font-semibold" data-id="element-194">
                  No tienes que sanar todo hoy. Solo tienes que dar el primer
                  paso.
                </p>

                <motion.a href="https://www.mialmazen.com/cart/47396884742282:1" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="w-full bg-gradient-to-r from-rose-600 to-purple-600 text-white py-6 rounded-full text-[clamp(1.25rem,3vw,1.5rem)] font-bold shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center justify-center gap-1" data-id="element-195">
                  <span className="flex items-center gap-2" data-id="element-196">🛒 Sí, quiero</span>
                  <span data-id="element-197">salir del ciclo y volver a mí</span>
                </motion.a>

                {/* Trust Badges */}
                <div className="mt-8" data-id="element-198">
                  <ul className="space-y-2" data-id="element-199">
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-200">
                      <span className="text-green-600 mr-2" data-id="element-201">✔</span>
                      <span data-id="element-202">Acceso inmediato</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-203">
                      <span className="text-green-600 mr-2" data-id="element-204">✔</span>
                      <span data-id="element-205">Pago seguro con Shopify</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-206">
                      <span className="text-green-600 mr-2" data-id="element-207">✔</span>
                      <span data-id="element-208">Descargable y privado</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-209">
                      <span className="text-green-600 mr-2" data-id="element-210">✔</span>
                      <span data-id="element-211">Acceso desde cualquier dispositivo</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final Message */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-rose-50 to-purple-50" data-id="element-212">
        <div className="max-w-4xl mx-auto" data-id="element-213">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center space-y-4" data-id="element-214">
            <p className="text-[clamp(1.25rem,3vw,1.75rem)] text-gray-800" data-id="element-215">
              🖤 No estás sola.
            </p>
            <p className="text-[clamp(1.25rem,3vw,1.75rem)] text-gray-800" data-id="element-216">
              🖤 No estás rota.
            </p>
            <p className="text-[clamp(1.25rem,3vw,1.75rem)] text-gray-800 font-bold" data-id="element-217">
              🖤 Estás despertando.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}